import { StandardEvents } from '@shopify/events';

/**
 * Add-to-cart guard — one click, one add.
 *
 * Horizon's product form (assets/product-form.js) sends a new /cart/add
 * request on every submit and never locks the button while it is in flight;
 * its "added" tick lasts 0.8 s. Double-clicks (a habit of many shoppers,
 * Baymard) and impatient repeat clicks during the 1–3 s round-trip each add
 * another unit — a shopper ended up with 18 mattresses.
 *
 * This module locks the form's add-to-cart buttons the instant the form is
 * submitted and releases them when Shopify answers — Horizon dispatches the
 * standard `cartLinesUpdate` event whose `promise` settles with the cart
 * response — with a short minimum so the confirmation is seen. While locked
 * the buttons are `disabled` (native: no click, no submit) and `aria-busy`
 * (spinner in assets/custom-body.css). It covers every Horizon product form:
 * the product page, its sticky bar (which submits the same form), quick-add
 * on cards and the quick-add modal. Adding more on purpose still works —
 * after the confirmation, or with the quantity selector.
 *
 * Runs before Horizon's own handlers: submit is caught on `window` in the
 * capture phase (Horizon delegates from `document`).
 */

const MIN_LOCK_MS = 900;
const MAX_LOCK_MS = 10000;

/** @param {HTMLFormElement} form */
const buttonsFor = (form) => {
  const component = form.closest('product-form-component');
  const scope = component ?? form;
  /** @type {HTMLButtonElement[]} */
  const buttons = [...scope.querySelectorAll('button[name="add"]')];
  // The product page's sticky bar submits this same form from outside it
  const section = form.closest('.shopify-section');
  const sticky = section?.querySelector('sticky-add-to-cart .sticky-add-to-cart__button');
  if (sticky instanceof HTMLButtonElement) buttons.push(sticky);
  return buttons;
};

/** @param {HTMLFormElement} form */
const lock = (form) => {
  const startedAt = performance.now();
  const buttons = buttonsFor(form);
  const wasDisabled = buttons.map((b) => b.disabled);
  form.dataset.qAdding = 'true';
  for (const b of buttons) {
    b.setAttribute('aria-busy', 'true');
    // Disable on the next task, after every submit listener has run: Horizon's
    // handler checks `disabled` and would refuse the very add that locked us
    // (microtasks run between listeners, so queueMicrotask is too early)
    setTimeout(() => {
      if (form.dataset.qAdding === 'true') b.disabled = true;
    }, 0);
  }

  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    const wait = Math.max(0, MIN_LOCK_MS - (performance.now() - startedAt));
    setTimeout(() => {
      delete form.dataset.qAdding;
      buttons.forEach((b, i) => {
        b.removeAttribute('aria-busy');
        // Restore what Horizon had (e.g. still disabled for a sold-out variant)
        b.disabled = wasDisabled[i] ?? false;
      });
    }, wait);
  };
  setTimeout(release, MAX_LOCK_MS);
  return release;
};

/** @type {WeakMap<HTMLElement, () => void>} */
const pending = new WeakMap();

window.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.closest('product-form-component')) return;
    if (form.dataset.qAdding === 'true') {
      // Second submit while the first is still in flight: ignore it entirely
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    const component = /** @type {HTMLElement} */ (form.closest('product-form-component'));
    pending.set(component, lock(form));
  },
  true
);

// Horizon announces the add (and its answer, via `promise`)
document.addEventListener(
  StandardEvents.cartLinesUpdate,
  (event) => {
    const source = event.target instanceof Element ? event.target.closest('product-form-component') : null;
    const release = source ? pending.get(source) : undefined;
    if (!source || !release) return;
    pending.delete(source);
    const promise = /** @type {{ promise?: Promise<unknown> }} */ (/** @type {unknown} */ (event)).promise;
    if (promise) promise.then(release, release);
    else release();
  },
  true
);

// A failed add (sold out, quantity limit) also unlocks
document.addEventListener(
  StandardEvents.cartError,
  (event) => {
    const source = event.target instanceof Element ? event.target.closest('product-form-component') : null;
    const release = source ? pending.get(source) : undefined;
    if (source && release) {
      pending.delete(source);
      release();
    }
  },
  true
);

/*
 * "Choose options" (a card's button for products with options) fetches the
 * product before Horizon opens the quick-add pop-up — up to a couple of
 * seconds on a phone, with no sign anything happened, so the page felt stuck
 * and shoppers tapped again. The button shows the shared spinner (aria-busy,
 * assets/custom-body.css) from the tap until the pop-up is open, with a
 * safety release.
 *
 * Opening a modal focuses its first control — the × — and phones then drew a
 * focus box around it. When the pop-up was opened by touch or mouse, focus
 * goes to the sheet itself instead (still inside the modal for screen
 * readers; Tab reaches the × next). Keyboard opening keeps the visible focus.
 */
const CHOOSE_MAX_MS = 8000;

window.addEventListener(
  'click',
  (event) => {
    const button = event.target instanceof Element ? event.target.closest('.quick-add__button--choose') : null;
    if (!(button instanceof HTMLButtonElement) || button.getAttribute('aria-busy') === 'true') return;

    // detail > 0: a real click or tap (keyboard activation reports 0)
    const byPointer = /** @type {MouseEvent} */ (event).detail > 0;
    button.setAttribute('aria-busy', 'true');
    const startedAt = performance.now();
    const done = () => button.removeAttribute('aria-busy');
    /** @param {HTMLDialogElement} dialog */
    const focusSheet = (dialog) => {
      if (!dialog.open || !document.activeElement?.closest('.quick-add-modal__close')) return;
      dialog.tabIndex = -1;
      dialog.focus({ preventScroll: true });
    };
    const check = () => {
      const dialog = document.querySelector('dialog.quick-add-modal[open]');
      if (dialog instanceof HTMLDialogElement) {
        done();
        if (byPointer) {
          // Horizon may focus the × right after opening; settle after it
          focusSheet(dialog);
          setTimeout(() => focusSheet(dialog), 60);
          setTimeout(() => focusSheet(dialog), 400);
        }
      } else if (performance.now() - startedAt > CHOOSE_MAX_MS) done();
      else requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
  },
  true
);
